/**
 * Global-Tool Trigger Helpers (Phase 0 — fix/global-tool-trigger-hardening)
 *
 * Shared helpers for global-tool trigger patterns. Provides:
 *
 *   1. `hasNearbyNegation(text, matchIndex, matchLength, windowChars=25)` —
 *      In-clause negation guard. Returns true if the user's message has a
 *      negation phrase within ±windowChars of the regex match — meaning the
 *      regex matched a phrase the user explicitly said they were NOT
 *      asking about.
 *
 *   2. `makeRegexTrigger(patterns)` — Wraps a regex array into a trigger
 *      function with the negation guard applied.
 *
 *   3. `makeRegexTriggerWithExtractor(patterns, extractor)` — Same, but
 *      lets the caller post-process the match (extract query, fields, etc.).
 *
 *   4. `hasNegationAround(text, anchorRegex, windowChars=25)` — Convenience
 *      wrapper for tools whose trigger function is a fuzzy NL parser
 *      (deepResearch, translate, videoScript, browserUse, …) that need to
 *      apply the negation guard against a per-tool keyword anchor after
 *      their own logic detects a match.
 *
 * Background
 * ----------
 *
 * Slack thread C0AMX0ACS9K/1778451390.755569 (2026-05-09): a user asked
 *
 *   "do a full research and diagnosis of your environment, what tools you
 *    have access to or might possibly have access into the future …
 *    I'm not talking about roles and permissions"
 *
 * the RBAC-management tool's pattern 0 (that tool was DELETED on 2026-09-16,
 * D15 — the lesson below is why the guard exists and still binds every tool)
 *
 *   /\b(what|which|list|show|tell me|see)\b[^?]{0,60}\b(my\s+)?(permissions?|grants?|access|privileges?)\b/i
 *
 * pre-empted because `access` is overloaded ("tools you have access to") and
 * the negation "I'm not talking about roles and permissions" was ignored —
 * regex has no semantic understanding. Result: the user got a stock 9-role
 * RBAC dump instead of a capability survey.
 *
 * This module is the safe Phase 0 hardening:
 *
 *   • Window quantifiers cap at 30 (`[^.;\n]{0,30}` instead of `[^?]{0,60}`).
 *   • Negation guard (`hasNearbyNegation`) wraps every match so explicit
 *     "I'm not talking about X" / "anything except X" phrases suppress
 *     misroutes.
 *   • Tools must use `makeRegexTrigger` / `makeRegexTriggerWithExtractor`
 *     so the negation guard is applied uniformly across the registry.
 *
 * Phase 1 (orchestrator-authoritative routing) and Phase 2 (structured-
 * payload tools) ship as separate follow-up PRs. These helpers do NOT
 * remove the regex-pre-emption layer; they only constrain what it fires
 * on.
 */

// ────────────────────────────────────────────────────────────────────────────
// Negation phrase patterns
// ────────────────────────────────────────────────────────────────────────────

/**
 * Regex alternation matching common negation phrases that should suppress
 * a regex match if they fall within the ±window of the match index.
 *
 * Each alternation is anchored on a word boundary on the left so
 * incidental substrings (e.g. "knot", "pricing") don't fire it. Casing
 * is normalised by the caller via `.toLowerCase()` before testing.
 */
const NEGATION_PHRASE = new RegExp(
  [
    // "I'm not talking/asking/interested/about/after/looking for/trying to/here for X"
    "\\bnot\\s+(?:talking|asking|interested|about|after|requesting|looking\\s+for|trying\\s+to|here\\s+for)\\b",
    // "don't mean/need/want/care about/ask for"
    "\\bdon'?t\\s+(?:mean|need|want|care\\s+about|ask\\s+for)\\b",
    // explicit exclusion connectors
    "\\b(?:aside\\s+from|excluding|except\\s+for|except|nothing\\s+to\\s+do\\s+with|setting\\s+aside|other\\s+than|not\\s+including|outside\\s+of|besides)\\b",
    // "no roles" / "no permissions" / "no grants" / etc — narrow guard
    "\\bno(?:\\s+\\w+)?\\s+(?:roles?|permissions?|grants?|privileges?|members?)\\b",
    // ignore / skip
    "\\b(?:ignore|skip)\\b",
  ].join('|'),
  'i'
);

// ────────────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────────────

/**
 * In-clause negation guard. Returns true if the user's message has a
 * negation phrase within ±`windowChars` of the regex match — meaning the
 * trigger matched a phrase the user explicitly said they were NOT asking
 * about.
 *
 * Examples that should detect negation:
 *   "I'm not talking about roles and permissions"           → no permissions trigger
 *   "tell me what tools you have access to, aside from RBAC" → no rbac trigger
 *   "anything except role management"                        → no rbac trigger
 *   "nothing to do with permissions"                         → no rbac trigger
 *   "ignore the legal-doc creator and just answer"           → no legal-doc trigger
 *
 * Window logic: take the substring from `[max(0, matchIndex-windowChars)
 * .. matchIndex+matchLength+windowChars]`, lowercase it, and test the
 * negation phrase regex.
 *
 * @param text       Full original text (any casing)
 * @param matchIndex Index of the regex match within `text`
 * @param matchLength Length of the matched substring
 * @param windowChars Distance (chars) on either side of the match to
 *                   examine for negation phrases. Default 25.
 */
export function hasNearbyNegation(
  text: string,
  matchIndex: number,
  matchLength: number,
  windowChars: number = 25
): boolean {
  if (!text || matchIndex < 0) return false;
  const start = Math.max(0, matchIndex - windowChars);
  const end = Math.min(text.length, matchIndex + matchLength + windowChars);
  const window = text.slice(start, end).toLowerCase();
  return NEGATION_PHRASE.test(window);
}

/**
 * Result shape returned by trigger functions. `query` is the trimmed
 * trigger text by default; tools that need extra fields (translate,
 * deepResearch) can use `makeRegexTriggerWithExtractor` to attach them.
 *
 * Includes an open-ended index signature for compatibility with the
 * registry's `TriggerMatch` type, which permits tool-specific extras.
 */
export interface BasicTriggerMatch {
  matches: boolean;
  query?: string;
  [key: string]: unknown;
}

/**
 * Wrap a regex array into a trigger function with negation handling.
 * Returns the first non-negated match; ignores any match whose ±25-char
 * window contains a negation phrase.
 *
 * @example
 *   const TRIGGERS = [
 *     /\b(list|show)\s+(my\s+)?(permissions?|roles?)\b/i,
 *     /\b(assign|revoke)\s+\w+\s+role\b/i,
 *   ];
 *   const matchTrigger = makeRegexTrigger(TRIGGERS);
 */
export function makeRegexTrigger(
  patterns: RegExp[]
): (text: string) => BasicTriggerMatch {
  return (text: string): BasicTriggerMatch => {
    const trimmed = (text || '').trim();
    if (!trimmed) return { matches: false };
    for (const re of patterns) {
      const m = trimmed.match(re);
      if (!m || typeof m.index !== 'number') continue;
      if (hasNearbyNegation(trimmed, m.index, m[0].length)) continue;
      return { matches: true, query: trimmed };
    }
    return { matches: false };
  };
}

/**
 * Same as `makeRegexTrigger` but lets the caller post-process the match
 * (e.g. extract a captured group, derive a target language, etc.). The
 * extractor is called only for non-negated matches.
 *
 * @example
 *   const matchTrigger = makeRegexTriggerWithExtractor(
 *     [/^translate\s+(?:to\s+)?(\w+)\s*[:\-]?\s*(.+)?$/i],
 *     (text, m) => ({ targetLanguage: m[1], inlineContent: m[2] })
 *   );
 */
export function makeRegexTriggerWithExtractor<T extends object>(
  patterns: RegExp[],
  extractor: (text: string, match: RegExpMatchArray) => T
): (text: string) => ({ matches: false } | ({ matches: true } & T)) {
  return (text: string) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return { matches: false } as { matches: false };
    for (const re of patterns) {
      const m = trimmed.match(re);
      if (!m || typeof m.index !== 'number') continue;
      if (hasNearbyNegation(trimmed, m.index, m[0].length)) continue;
      const extra = extractor(trimmed, m);
      return { matches: true, ...extra } as { matches: true } & T;
    }
    return { matches: false } as { matches: false };
  };
}

/**
 * For tools whose trigger function is a fuzzy NL parser (deepResearch,
 * translate, videoScript, …): after the parser declares a match, scan
 * `text` for the first occurrence of any `anchorRegex` keyword and apply
 * `hasNearbyNegation` against that anchor. Returns true if a negation
 * was found near the anchor; the caller should then return
 * `{ matches: false }`.
 *
 * Pass an `anchorRegex` whose alternation captures the tool's domain
 * keywords (e.g. /\b(deep\s+research|investigate|analyze)\b/i for
 * deepResearch). If the regex doesn't match, the function returns false
 * (no negation detected — keep the original match).
 */
export function hasNegationAround(
  text: string,
  anchorRegex: RegExp,
  windowChars: number = 25
): boolean {
  if (!text) return false;
  const m = text.match(anchorRegex);
  if (!m || typeof m.index !== 'number') return false;
  return hasNearbyNegation(text, m.index, m[0].length, windowChars);
}

// (Lifted from the deleted orchestrator/utils.ts — D13-6b, 2026-08-25.)
/**
 * True when the user's message explicitly instructs the assistant not to ask
 * clarifying questions, or to proceed with the context already provided.
 *
 * Deterministic backstop for the deep-research clarify interstitial
 * (2026-08-07, July 8 feature test): the orchestrator prompt alone could not
 * be trusted to set skipClarification, so deepResearch.ts ORs this check over
 * the raw user text. Mirrors the two server-side patterns in
 * functions/src/prest0nSession/translationDeliverableHook.ts
 * (no_clarifying_questions_explicit + proceed_with_available_context —
 * functions/src is not importable from src, hence the twin). Keep the regexes
 * in lockstep when editing either side.
 */
export function detectNoClarifyInstruction(rawUserText: string): boolean {
  if (typeof rawUserText !== 'string' || !rawUserText.trim()) return false;
  const normalized = rawUserText.replace(/[ \t\f\v]+/g, ' ').trim();
  const NO_CLARIFY_RES: RegExp[] = [
    /\b(?:no\s+clarifying\s+questions|skip\s+clarifying(?:\s+questions)?|sin\s+preguntas\s+aclaratorias|sin\s+preguntas\s+de\s+aclaraci[oó]n|no\s+preguntas\s+aclaratorias)\b/i,
    /\b(?:(?:do\s+not|don['’]t|no\s+need\s+to|without)\s+ask(?:ing)?(?:\s+(?:me|us))?(?:\s+any)?(?:\s+(?:more|further|additional))?\s+(?:clarif(?:ying|ication)\s+)?questions?|ask\s+no\s+(?:clarifying\s+)?questions?|no\s+(?:me\s+)?hagas\s+(?:m[aá]s\s+)?preguntas(?:\s+aclaratorias)?|no\s+hagan\s+preguntas\s+aclaratorias)\b/i,
    /\b(?:proceed|continue|go\s+ahead|start|begin)\s+with\s+(?:the\s+)?(?:available|existing|current|provided)\s+(?:context|information|info|details)\b|\bproceed\s+with\s+what\s+you\s+(?:have|know)\b|\b(?:procede|contin[uú]a|adelante)\s+con\s+(?:el\s+contexto|la\s+informaci[oó]n)\s+(?:disponible|existente|actual|proporcionad[ao])\b/i,
  ];
  return NO_CLARIFY_RES.some((re) => re.test(normalized));
}
